import {
  formatConditionValueDisplay,
  hasConditionValue,
  type RuleConditionValue,
} from './valueUtils'

export type { RuleConditionValue }

/**
 * Rule field. Values must match backend RuleField choices.
 */
export type RuleField =
  | 'worksite_name'
  | 'worksite_location'
  | 'additional_info'
  | 'title'
  | 'employment_type'
  | 'employment_subtype'

/**
 * Operator for matching the field value. Values must match backend RuleOperator choices.
 */
export type RuleOperator =
  | 'equals'
  | 'contains'
  | 'not_contains'
  | 'not_equals'
  | 'is_any_of'
  | 'is_none_of'

/** How primary and secondary conditions combine. Matches backend `condition_join`. */
export type ConditionJoin = 'and' | 'or'

export const FIELD_LABELS: Record<RuleField, string> = {
  worksite_name: 'Worksite name',
  worksite_location: 'Worksite location',
  additional_info: 'Additional info',
  title: 'Title',
  employment_type: 'Employment type',
  employment_subtype: 'Employment subtype',
}

export const OPERATOR_LABELS: Record<RuleOperator, string> = {
  equals: 'Equals',
  contains: 'Contains',
  not_contains: 'Not contains',
  not_equals: 'Not equals',
  is_any_of: 'Is any of',
  is_none_of: 'Is none of',
}

export const CONDITION_JOIN_LABELS: Record<ConditionJoin, string> = {
  and: 'AND',
  or: 'OR',
}

export interface RuleConditionsShape {
  field: RuleField
  operator: RuleOperator
  value: RuleConditionValue
  secondaryField?: RuleField | null
  secondaryOperator?: RuleOperator | null
  secondaryValue?: RuleConditionValue | null
  conditionJoin?: ConditionJoin | null
  tertiaryField?: RuleField | null
  tertiaryOperator?: RuleOperator | null
  tertiaryValue?: RuleConditionValue | null
  tertiaryConditionJoin?: ConditionJoin | null
}

/** Human-readable summary for condition display. */
export function formatRuleConditions(rule: RuleConditionsShape): string {
  const primary = `${FIELD_LABELS[rule.field]} ${OPERATOR_LABELS[rule.operator]} ${formatConditionValueDisplay(rule.value, rule.operator)}`
  const secOp = rule.secondaryOperator ?? 'equals'
  const secVal = rule.secondaryValue ?? ''
  if (!rule.secondaryField || !hasConditionValue(secVal, secOp)) {
    return primary
  }
  const join =
    rule.conditionJoin === 'or'
      ? CONDITION_JOIN_LABELS.or
      : CONDITION_JOIN_LABELS.and
  const secondary = `${FIELD_LABELS[rule.secondaryField]} ${OPERATOR_LABELS[secOp]} ${formatConditionValueDisplay(secVal, secOp)}`
  let summary = `${primary} ${join} ${secondary}`
  const terOp = rule.tertiaryOperator ?? 'equals'
  const terVal = rule.tertiaryValue ?? ''
  if (rule.tertiaryField && hasConditionValue(terVal, terOp)) {
    const terJoin =
      rule.tertiaryConditionJoin === 'or'
        ? CONDITION_JOIN_LABELS.or
        : CONDITION_JOIN_LABELS.and
    const tertiary = `${FIELD_LABELS[rule.tertiaryField]} ${OPERATOR_LABELS[terOp]} ${formatConditionValueDisplay(terVal, terOp)}`
    summary = `${summary} ${terJoin} ${tertiary}`
  }
  return summary
}
